import { Archive, Inbox, PenSquare, Send, Star, Trash2, type LucideIcon } from 'lucide-react';
import type { MailFolder, MailMailboxDto } from '../../lib/mailTypes';

const MAIL_FOLDERS: { id: MailFolder; label: string; icon: LucideIcon }[] = [
  { id: 'inbox', label: 'Inbox', icon: Inbox },
  { id: 'sent', label: 'Sent', icon: Send },
  { id: 'archive', label: 'Archive', icon: Archive },
  { id: 'trash', label: 'Trash', icon: Trash2 },
];

export function MailSidebar({
  mailboxes,
  mailboxId,
  folder,
  starredOnly,
  onSelectMailbox,
  onSelectFolder,
  onToggleStarred,
  onCompose,
}: {
  mailboxes: MailMailboxDto[];
  mailboxId: string | null;
  folder: MailFolder;
  starredOnly: boolean;
  onSelectMailbox: (id: string) => void;
  onSelectFolder: (folder: MailFolder) => void;
  onToggleStarred: () => void;
  onCompose?: () => void;
}) {
  const current = mailboxes.find((m) => m.id === mailboxId);
  return (
    <nav className="mail-sidebar" aria-label="Mail navigation">
      {onCompose && (
        <button type="button" className="btn btn-primary mail-compose-new" onClick={onCompose}>
          <PenSquare size={14} aria-hidden /> New message
        </button>
      )}
      <div className="mail-mailboxes" role="group" aria-label="Mailboxes">
        {mailboxes.map((m) => (
          <button
            key={m.id}
            type="button"
            className={m.id === mailboxId ? 'mail-mailbox active' : 'mail-mailbox'}
            aria-pressed={m.id === mailboxId}
            onClick={() => onSelectMailbox(m.id)}
            title={m.address}
          >
            <span className="mail-mailbox-name">
              {m.displayName}
              <span className="cell-sub">{m.type === 'shared' ? 'Shared' : m.address}</span>
            </span>
            {m.unreadCount > 0 && (
              <span className="mail-badge" aria-label={`${m.unreadCount} unread`}>
                {m.unreadCount > 99 ? '99+' : m.unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>
      {current && (
        <div className="mail-folders" role="group" aria-label="Folders">
          {MAIL_FOLDERS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={id === folder ? 'mail-folder active' : 'mail-folder'}
              aria-current={id === folder ? 'page' : undefined}
              onClick={() => onSelectFolder(id)}
            >
              <Icon size={16} aria-hidden />
              <span>{label}</span>
              {id === 'inbox' && current.unreadCount > 0 && (
                <span className="mail-badge">{current.unreadCount > 99 ? '99+' : current.unreadCount}</span>
              )}
            </button>
          ))}
          <button
            type="button"
            className={starredOnly ? 'mail-folder active' : 'mail-folder'}
            aria-pressed={starredOnly}
            onClick={onToggleStarred}
            title="Show only starred conversations among those loaded"
          >
            <Star size={16} aria-hidden />
            <span>Starred</span>
          </button>
        </div>
      )}
    </nav>
  );
}
